import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Org, OrgMember } from '@shopgnd/shared';
import { ensureWorkspaceForUser, loadWorkspaceForUser, type WorkspaceContext } from '@/api/orgs';
import { isAdminImpersonating } from '@/lib/adminImpersonation';
import { mergeOrgCapabilities } from '@/lib/orgCapabilities';
import { useAuthStore } from '@/stores/authStore';
import { useVendorProfile } from '@/hooks/useVendorForUser';
import { useProProfile } from '@/hooks/useProForUser';
import { hubQueryKeys } from '@/lib/hubQueryClient';

function enrichWorkspaceCapabilities(
  ctx: WorkspaceContext,
  vendor: ReturnType<typeof useVendorProfile>['vendor'],
  pro: ReturnType<typeof useProProfile>['pro'],
): WorkspaceContext {
  const capabilities = mergeOrgCapabilities(ctx.org, vendor, pro);
  const prev = ctx.org.capabilities ?? [];
  if (prev.length === capabilities.length && prev.every((c) => capabilities.includes(c))) {
    return ctx;
  }
  return { ...ctx, org: { ...ctx.org, capabilities } };
}

export function useWorkspace(): {
  workspace: WorkspaceContext | null;
  org: Org | null;
  member: OrgMember | null;
  ready: boolean;
  isPersistedOrg: boolean;
} {
  const user = useAuthStore((s) => s.user);
  const { vendor, ready: vendorReady } = useVendorProfile();
  const { pro, ready: proReady } = useProProfile();

  const impersonating = isAdminImpersonating();

  const { data: workspace, isLoading } = useQuery({
    queryKey: hubQueryKeys.workspace(user?.id ?? '', vendor?.id, pro?.id),
    enabled: Boolean(user?.id) && vendorReady && proReady && !impersonating,
    staleTime: 0,
    retry: 2,
    queryFn: async () => {
      const loaded = await loadWorkspaceForUser(user!.id);
      if (loaded) return loaded;
      const ensured = await ensureWorkspaceForUser(user!.id, { vendor, pro });
      if (ensured) return ensured;
      if (vendor || pro) {
        throw new Error('Could not provision organization workspace.');
      }
      return null;
    },
  });

  const enriched = useMemo(() => {
    if (!workspace) return null;
    return enrichWorkspaceCapabilities(workspace, vendor, pro);
  }, [workspace, vendor, pro]);

  return {
    workspace: enriched,
    org: enriched?.org ?? null,
    member: enriched?.member ?? null,
    ready: vendorReady && proReady && !isLoading,
    isPersistedOrg: Boolean(workspace),
  };
}
